'use client';

import { c as _c } from "react/compiler-runtime";
import { trackEvent } from '@svenskaspelab/nova-core/nextjs/analytics';
import { loginRedirect } from '@svenskaspelab/nova-core/nextjs/auth';
import { useIsCouponWagerPending } from '@svenskaspelab/poolbetting/core/frameworks/nextjs/coupon';
import type { CouponStatus } from '@svenskaspelab/poolbetting/schema/coupon';
import { Betslip, BetslipLabel, BetslipNumberOfRows, BetslipPayButton, BetslipPrice } from '@svenskaspelab/poolbetting/ui/Betslip';
import { ShowHideButton } from '@svenskaspelab/poolbetting/ui/ShowHideButton';
import Balance from '@svenskaspelab/ui/react/icons/Balance';
import { type ReactNode, useEffect, useRef } from 'react';
const handleOpenDrawerClick = (): void => {
  trackEvent({
    category: 'Nya tipsen',
    action: 'Coupon overview',
    optLabel: 'Powerplay'
  });
};
type BetslipSrowProps = {
  betslipProps: {
    isPopulatedCoupon: boolean;
    hasAdditions: boolean;
    betslipContent: ReactNode;
    balance: number;
    shouldRedirectUser?: boolean;
    nrOfRows: number;
    totalCouponPrice: string;
    couponStatus: CouponStatus;
    returnUrl?: string;
  };
};

/**
 * Component för standard betslip.
 *
 * @param root0 - The props object.
 * @param root0.betslipProps - Correct props for the betslip standard component.
 * @returns standard betslip component
 */
function BetslipSrow(t0) {
  const $ = _c(30);
  const {
    betslipProps
  } = t0;
  const {
    isPopulatedCoupon,
    hasAdditions,
    betslipContent,
    balance,
    shouldRedirectUser,
    nrOfRows,
    totalCouponPrice,
    couponStatus,
    returnUrl
  } = betslipProps;
  const isPopulatedCouponRef = useRef(isPopulatedCoupon);
  const couponStatusRef = useRef(couponStatus);
  let t1;
  let t2;
  if ($[0] !== couponStatus || $[1] !== isPopulatedCoupon) {
    t1 = () => {
      if (isPopulatedCoupon !== isPopulatedCouponRef.current) {
        if (!isPopulatedCouponRef.current) {
          trackEvent({
            category: "Wager",
            action: "Betslip started",
            optLabel: "Powerplay"
          });
        }
        isPopulatedCouponRef.current = isPopulatedCoupon;
      }
      if (couponStatus !== couponStatusRef.current) {
        if (couponStatus === "VALID" && couponStatusRef.current !== "VALID") {
          trackEvent({
            category: "Wager",
            action: "Betslip complete",
            optLabel: "Powerplay"
          });
        }
        couponStatusRef.current = couponStatus;
      }
    };
    t2 = [isPopulatedCoupon, couponStatus];
    $[0] = couponStatus;
    $[1] = isPopulatedCoupon;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const isPending = useIsCouponWagerPending();
  let t3;
  if ($[4] !== returnUrl || $[5] !== shouldRedirectUser) {
    t3 = shouldRedirectUser ? () => {
      loginRedirect({
        returnUrl,
        cancelUrl: returnUrl
      });
    } : undefined;
    $[4] = returnUrl;
    $[5] = shouldRedirectUser;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleBalanceClick = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["[grid-template-areas:'label_nrOfRows_price''payButton_payButton_payButton']", "grid-cols-[min-content_auto_minmax(40px,_80px)]", "lg:[grid-template-areas:'label_nrOfRows_balance_price_payButton']", "lg:grid-cols-[max-content_auto_minmax(60px,_120px)_minmax(40px,_80px)_minmax(154px,_250px)]"];
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let betslipClassNames = t4;
  const shouldHidePayButton = couponStatus !== "VALID";
  if (shouldHidePayButton) {
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = ["[grid-template-areas:'label_nrOfRows_price']", "grid-cols-[min-content_auto_minmax(40px,_80px)]", "lg:[grid-template-areas:'label_nrOfRows_balance_price']", "lg:grid-cols-[max-content_auto_minmax(60px,_120px)_minmax(40px,_80px)]"];
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    betslipClassNames = t5;
  }
  const t5 = betslipClassNames.join(" ");
  const t6 = shouldHidePayButton ? "44px" : undefined;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <BetslipLabel label="Enkelrader" />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== nrOfRows) {
    t8 = <BetslipNumberOfRows numberOfRows={nrOfRows} />;
    $[10] = nrOfRows;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = `${balance} kr`;
  let t10;
  if ($[12] !== handleBalanceClick || $[13] !== t9) {
    t10 = <ShowHideButton className="text-layer-fg-muted hidden font-normal [grid-area:balance] lg:grid lg:grid-cols-[max-content_auto]" showValue={t9} hideValue="Visa saldo" variant="ghost" onClick={handleBalanceClick} Icon={Balance} />;
    $[12] = handleBalanceClick;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== hasAdditions || $[16] !== isPending || $[17] !== shouldHidePayButton) {
    t11 = !shouldHidePayButton && <BetslipPayButton hasAdditions={hasAdditions} disabled={isPending} />;
    $[15] = hasAdditions;
    $[16] = isPending;
    $[17] = shouldHidePayButton;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== totalCouponPrice) {
    t12 = <BetslipPrice price={totalCouponPrice} />;
    $[19] = totalCouponPrice;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== betslipContent || $[22] !== isPopulatedCoupon || $[23] !== t10 || $[24] !== t11 || $[25] !== t12 || $[26] !== t5 || $[27] !== t6 || $[28] !== t8) {
    t13 = <Betslip className={t5} isPopulatedCoupon={isPopulatedCoupon} betslipContent={betslipContent} betslipMobileHeight={t6} onOpenDrawerClick={handleOpenDrawerClick}>{t7}{t8}{t10}{t11}{t12}</Betslip>;
    $[21] = betslipContent;
    $[22] = isPopulatedCoupon;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t5;
    $[27] = t6;
    $[28] = t8;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}
export default BetslipSrow;